<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { NButton, NEmpty, NInput, NModal } from 'naive-ui'
import { useWorkspaceStore } from '@/store/workspace'
import { blurModalSearchFocus } from '@/utils/blurModalSearchFocus'
import {
  buildVariableIndexItems,
  clampVariableIndex,
  VARIABLE_INDEX_GROUP_SIZE,
  type VariableIndexScope,
} from '@/features/event/variableIndex'

/**
 * 变量索引选择弹窗
 *
 * 左侧按编号分组，右侧展示变量列表，支持搜索、单击选中与双击确认。
 */
const props = defineProps<{
  show: boolean
  title?: string
  selectedIndex: number
  scope: VariableIndexScope
  allowNegativeOne?: boolean
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  select: [value: number]
}>()

const workspace = useWorkspaceStore()

/** 搜索关键词。 */
const searchText = ref('')
/** 当前分组起始位置（列表非搜索模式）。 */
const selectedGroupStart = ref(0)
/** 弹窗内当前高亮索引。 */
const currentIndex = ref(0)
/** 左侧分组列表容器 ref。 */
const groupListRef = ref<HTMLElement | null>(null)
/** 右侧变量列表容器 ref。 */
const itemListRef = ref<HTMLElement | null>(null)

/** 当前 scope 下全部可选变量项。 */
const items = computed(() =>
  buildVariableIndexItems(workspace.project, props.scope, {
    allowNegativeOne: props.allowNegativeOne,
  }),
)

/** 弹窗标题（优先 props.title，否则按 scope 默认）。 */
const modalTitle = computed(() => {
  if (props.title?.trim()) return props.title.trim()
  if (props.scope === 'var') return '选择数值变量'
  if (props.scope === 'varEx') return '选择二周目变量'
  return '选择字符串'
})

/** 左侧编号分组选项。 */
const groupOptions = computed(() => {
  const out: Array<{ start: number; end: number; label: string }> = []
  for (let start = 0; start < items.value.length; start += VARIABLE_INDEX_GROUP_SIZE) {
    const end = Math.min(start + VARIABLE_INDEX_GROUP_SIZE, items.value.length)
    const first = items.value[start]
    const last = items.value[end - 1]
    out.push({
      start,
      end,
      label: `[ ${String(first?.order ?? 0).padStart(3, '0')} - ${String(last?.order ?? 0).padStart(3, '0')} ]`,
    })
  }
  return out
})

/** 右侧当前可见变量项（搜索模式或分组切片）。 */
const visibleItems = computed(() => {
  const keyword = searchText.value.trim().toLowerCase()
  if (keyword) {
    return items.value.filter((item) => item.searchText.includes(keyword) || item.label.toLowerCase().includes(keyword))
  }
  return items.value.slice(selectedGroupStart.value, selectedGroupStart.value + VARIABLE_INDEX_GROUP_SIZE)
})

/** 当前高亮索引对应的变量项。 */
const selectedItem = computed(() => items.value.find((item) => item.index === currentIndex.value) ?? null)

/** 关闭弹窗。 */
function closeModal() {
  emit('update:show', false)
}

/**
 * 切换左侧分组。
 * @param start 分组起始下标
 */
function selectGroup(start: number) {
  selectedGroupStart.value = start
}

/**
 * 单击选中变量项，非搜索模式下同步左侧分组。
 * @param index 变量索引
 */
function selectItem(index: number) {
  currentIndex.value = index
  if (!searchText.value.trim()) {
    const position = items.value.findIndex((item) => item.index === index)
    if (position >= 0) {
      selectedGroupStart.value = Math.floor(position / VARIABLE_INDEX_GROUP_SIZE) * VARIABLE_INDEX_GROUP_SIZE
    }
  }
}

/** 将当前分组与选中项滚动到可视区域。 */
async function scrollSelectionIntoView() {
  await nextTick()
  const groupTarget = groupListRef.value?.querySelector<HTMLElement>(
    `[data-group-start="${CSS.escape(String(selectedGroupStart.value))}"]`,
  )
  const itemTarget = itemListRef.value?.querySelector<HTMLElement>(
    `[data-variable-index="${CSS.escape(String(currentIndex.value))}"]`,
  )
  groupTarget?.scrollIntoView({ block: 'nearest' })
  itemTarget?.scrollIntoView({ block: 'nearest' })
}

/** 确认当前选中索引并关闭弹窗。 */
function confirmSelect() {
  emit('select', currentIndex.value)
  closeModal()
}

/**
 * 选中指定索引并立即确认。
 * @param index 变量索引
 */
function selectAndConfirm(index: number) {
  currentIndex.value = index
  emit('select', index)
  closeModal()
}

/**
 * 双击列表项时确认选择。
 * @param index 变量索引
 * @param event 鼠标事件
 */
function handleItemDoubleClick(index: number, event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
  selectAndConfirm(index)
}

watch(
  () => props.show,
  (show) => {
    if (!show) return
    searchText.value = ''
    currentIndex.value = clampVariableIndex(props.selectedIndex, workspace.project, props.scope, {
      allowNegativeOne: props.allowNegativeOne,
    })
    const position = Math.max(
      0,
      items.value.findIndex((item) => item.index === currentIndex.value),
    )
    selectedGroupStart.value = Math.floor(position / VARIABLE_INDEX_GROUP_SIZE) * VARIABLE_INDEX_GROUP_SIZE
    void scrollSelectionIntoView()
  },
  { immediate: true },
)
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    class="variable-index-picker-modal"
    :title="modalTitle"
    :auto-focus="false"
    :style="{ width: '640px' }"
    @after-enter="blurModalSearchFocus('.variable-index-picker-modal')"
    @update:show="emit('update:show', $event)"
  >
    <div class="variable-index-picker">
      <div class="variable-index-picker__toolbar">
        <n-input
          v-model:value="searchText"
          size="small"
          clearable
          :autofocus="false"
          placeholder="输入编号或名称快速查找"
        />
      </div>

      <div class="variable-index-picker__layout">
        <section class="variable-index-picker__panel variable-index-picker__panel--groups">
          <div class="variable-index-picker__caption">编号分组</div>
          <div v-if="groupOptions.length" ref="groupListRef" class="picker-scroll-list picker-scroll-list--padded">
            <button
              v-for="group in groupOptions"
              :key="`variable-index-group-${group.start}`"
              :data-group-start="group.start"
              type="button"
              class="picker-scroll-item picker-scroll-item--surface"
              :class="{ 'picker-scroll-item--active': group.start === selectedGroupStart }"
              @click="selectGroup(group.start)"
            >
              {{ group.label }}
            </button>
          </div>
          <n-empty v-else description="当前没有可选变量" size="small" />
        </section>

        <section class="variable-index-picker__panel variable-index-picker__panel--list">
          <div class="variable-index-picker__caption">变量列表</div>
          <div v-if="visibleItems.length" ref="itemListRef" class="picker-scroll-list picker-scroll-list--padded">
            <button
              v-for="item in visibleItems"
              :key="`variable-index-item-${item.index}`"
              :data-variable-index="item.index"
              type="button"
              class="picker-scroll-item picker-scroll-item--surface"
              :class="{ 'picker-scroll-item--active': item.index === currentIndex }"
              @click="selectItem(item.index)"
              @dblclick="handleItemDoubleClick(item.index, $event)"
            >
              {{ item.label }}
            </button>
          </div>
          <n-empty v-else description="没有匹配的变量" size="small" />
        </section>

      </div>

      <div class="variable-index-picker__actions">
        <n-button size="small" @click="closeModal">取消</n-button>
        <n-button size="small" type="primary" :disabled="!selectedItem" @click="confirmSelect">确定</n-button>
      </div>
    </div>
  </n-modal>
</template>

<style scoped>
::v-deep(.variable-index-picker-modal.n-modal) {
  width: 640px;
  max-width: 640px;
}

.variable-index-picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  height: 520px;
  min-height: 0;
  overflow: hidden;
}

.variable-index-picker__toolbar {
  flex: 0 0 auto;
}

.variable-index-picker__layout {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.variable-index-picker__panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

.variable-index-picker__caption {
  padding: 6px 8px;
  border-bottom: 1px solid var(--theme-border);
  color: var(--theme-text-secondary);
  font-size: 12px;
}

.variable-index-picker__actions {
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-end;
  gap: 6px;
  padding-top: 4px;
  border-top: 1px solid var(--theme-border);
}
</style>
